import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteProjectDialog } from '../DeleteProjectDialog';
import { EditProjectDialog } from '../EditProjectDialog';

dayjs.extend(relativeTime);

type Project = {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
};

const ProjectActionsDropdown = ({ projectId }: { projectId: string }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setIsEditOpen(true)}>
            <Pencil className="h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditProjectDialog
        projectId={projectId}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
      <DeleteProjectDialog
        projectId={projectId}
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
      />
    </>
  );
};

export const useProjectsColumns = (): ColumnDef<Project>[] => {
  return [
    {
      accessorKey: 'name',
      header: () => (
        <Text variant="body-sm" color="primary">
          Name
        </Text>
      ),
      cell: ({ row }) => {
        const navigate = useNavigate();
        return (
          <FlexRow
            gap="2"
            alignItems="center"
            className="cursor-pointer hover:underline"
            onClick={() => navigate(row.original.id)}
          >
            <Ellipsis className="max-w-[50ch] w-full" asChild hasTooltip>
              <Text variant="body-sm" color="primary">
                {row.original.name}
              </Text>
            </Ellipsis>
          </FlexRow>
        );
      },
    },
    {
      accessorKey: 'description',
      header: () => (
        <Text variant="body-sm" color="primary">
          Description
        </Text>
      ),
      cell: ({ row }) => {
        const navigate = useNavigate();
        return (
          <FlexRow
            gap="2"
            alignItems="center"
            className="cursor-pointer"
            onClick={() => navigate(row.original.id)}
          >
            <Ellipsis className="max-w-[40ch] w-full" asChild hasTooltip>
              <Text variant="body-sm" color="secondary">
                {row.original.description || '-'}
              </Text>
            </Ellipsis>
          </FlexRow>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <Text variant="body-sm" color="primary">
          Created
        </Text>
      ),
      cell: ({ row }) => {
        const navigate = useNavigate();
        return (
          <FlexRow
            gap="2"
            alignItems="center"
            className="cursor-pointer hover:underline"
            onClick={() => navigate(row.original.id)}
          >
            <Text variant="body-sm" color="secondary">
              {row.original.createdAt
                ? dayjs(row.original.createdAt).fromNow()
                : '-'}
            </Text>
          </FlexRow>
        );
      },
    },
    {
      accessorKey: 'actions',
      meta: {
        headerClassName: 'w-10',
        cellClassName: 'w-10',
      },
      header: () => null,
      cell: ({ row }) => <ProjectActionsDropdown projectId={row.original.id} />,
    },
  ];
};
